/**
 * One microphone stream for the whole app (owner, 2026-10-14: the
 * microphone is asked for once, not per lesson). Each lesson page used to
 * open its own stream on the first take and stop it on leaving, so the
 * next lesson started over. Now the stream outlives the page:
 *
 * - `getMicStream()` hands out the live stream, opening it only when there
 *   is none (or its track ended — unplugged, or the grant was taken back);
 * - a lesson page `holdMic()`s while it is open and `releaseMic()`s on
 *   leaving; with nobody holding, the stream stops after IDLE_RELEASE_MS,
 *   so the next lesson within that time reuses it and the browser's
 *   recording indicator does not stay on for good;
 * - a hidden tab or a page being unloaded stops it at once — unless a take
 *   is being recorded (`setMicRecording`), which must not be cut short.
 *
 * The R13 live-text recognizer listens to the same recording graph, so it
 * uses this stream too.
 */

export const IDLE_RELEASE_MS = 60_000

export const MIC_CONSTRAINTS = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    // A hint, not a guarantee — most browsers still hand the AudioContext
    // its own hardware rate, which is why encodeWav16kMono resamples.
    channelCount: 1,
    sampleRate: 16000,
  },
}

let stream = null
let pending = null
let holders = 0
let recording = false
let timer = null
let watching = false

function isLive(candidate) {
  return Boolean(candidate?.getAudioTracks?.().some((track) => track.readyState === 'live'))
}

function clearTimer() {
  if (timer) clearTimeout(timer)
  timer = null
}

/** Stop the microphone now (the browser's indicator goes off). The next take opens it again. */
export function stopMicStream() {
  clearTimer()
  stream?.getTracks().forEach((track) => track.stop())
  stream = null
}

function watchPage() {
  if (watching || typeof document === 'undefined' || typeof window === 'undefined') return
  watching = true
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && !recording) stopMicStream()
  })
  window.addEventListener('pagehide', () => {
    if (!recording) stopMicStream()
  })
}

/**
 * The live microphone stream, opened if needed. Rejects as getUserMedia
 * does (NotAllowedError when refused, NotFoundError without a device).
 */
export async function getMicStream(getUserMedia = (constraints) => navigator.mediaDevices.getUserMedia(constraints)) {
  if (isLive(stream)) return stream
  if (!pending) {
    pending = getUserMedia(MIC_CONSTRAINTS)
      .then((opened) => {
        stream = opened
        watchPage()
        if (holders === 0 && !recording) scheduleRelease()
        return opened
      })
      .finally(() => {
        pending = null
      })
  }
  return pending
}

function scheduleRelease() {
  clearTimer()
  timer = setTimeout(stopMicStream, IDLE_RELEASE_MS)
}

/** A practice page is open: keep the stream while it is. */
export function holdMic() {
  holders += 1
  clearTimer()
}

/** The page closed: with nobody else holding it, the stream stops after IDLE_RELEASE_MS. */
export function releaseMic() {
  holders = Math.max(0, holders - 1)
  if (holders === 0 && stream && !recording) scheduleRelease()
}

/** A take is being recorded — a hidden tab must not cut it off. */
export function setMicRecording(value) {
  recording = Boolean(value)
}

/** Tests only: forget everything, page listeners included. */
export function resetMicStreamForTests() {
  stopMicStream()
  pending = null
  holders = 0
  recording = false
  watching = false
}
